'use client'

import { Button, ChevronLeft } from '@chain-zen/ui'

import type { IELDApp, IELDAsset } from '../../../../../../../data/queries/eld-apps'
import type { TActiveAssetType } from '../../hooks/use-eld-asset-selection'
import { AssetList } from './asset-list'
import { AssetListSkeleton } from './asset-list-skeleton'
import { AssetTypeTab } from './asset-type-tab'
import { ELDAppIcon } from './eld-app-icon'
import { ELDConfirmationModal } from './eld-confirmation-modal'

interface IAssetSelectionStepState {
  activeAssetType: TActiveAssetType
  assets: IELDAsset[]
  currentTrailerNumber?: string | null
  currentTruckNumber?: string | null
  currentValue?: string | null
  loadingAssets: boolean
  manualInputValue: string
  pendingAction?: { type: string; value: string | null } | null
  selectingAsset: string | null
  selectedELDApp: IELDApp | null
  selectedItem: IELDAsset | null
  showRemoveConfirmation: boolean
  showReplaceConfirmation: boolean
}

interface IAssetSelectionStepActions {
  onActiveAssetTypeChange: (type: TActiveAssetType) => void
  onAssetSelect: (asset: IELDAsset) => void
  onBack: () => void
  onCancelConfirmation: () => void
  onConfirmRemove: () => void
  onConfirmReplace: () => void
  onManualInputChange: (value: string) => void
  onManualSubmit: () => void
  onRemoveClick: () => void
}

interface IAssetSelectionStepProps {
  actions: IAssetSelectionStepActions
  isLoadingUpdate: boolean
  state: IAssetSelectionStepState
}

const getConfirmText = (isLoadingUpdate: boolean, isReplace: boolean): string => {
  if (isLoadingUpdate) return isReplace ? 'Replacing...' : 'Removing...'
  return isReplace ? 'Yes, Replace' : 'Yes, Remove'
}

const getConfirmationModalProps = (state: IAssetSelectionStepState, isLoadingUpdate: boolean) => {
  const isReplace = !!state.showReplaceConfirmation && !!state.pendingAction
  return {
    isReplace,
    isLoadingUpdate,
    confirmText: getConfirmText(isLoadingUpdate, isReplace)
  }
}

/**
 * Step 2 of the asset selection wizard: choose trailer or truck from selected ELD app.
 */
export const AssetSelectionStep = ({ actions, isLoadingUpdate, state }: IAssetSelectionStepProps) => {
  const { activeAssetType, selectedELDApp } = state
  const isConfirmationOpen = state.showRemoveConfirmation || state.showReplaceConfirmation
  const confirmationProps = getConfirmationModalProps(state, isLoadingUpdate)

  return (
    <div className="space-y-3">
      {/* Back button + Selected ELD */}
      <div className="flex items-center gap-2">
        <Button
          onClick={actions.onBack}
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-muted rounded-md">
          {!!selectedELDApp && <ELDAppIcon appType={selectedELDApp.appType} />}
          <span className="text-foreground text-sm">{selectedELDApp?.appType}</span>
        </div>
      </div>

      {/* Asset Type Tabs */}
      <div className="flex gap-2">
        <AssetTypeTab
          activeAssetType={activeAssetType}
          currentValue={state.currentTrailerNumber}
          label="Trailer"
          onSelect={() => actions.onActiveAssetTypeChange('trailer')}
          tabKey="trailer"
        />
        <AssetTypeTab
          activeAssetType={activeAssetType}
          currentValue={state.currentTruckNumber}
          label="Truck"
          onSelect={() => actions.onActiveAssetTypeChange('truck')}
          tabKey="truck"
        />
      </div>

      {!!isConfirmationOpen && (
        <ELDConfirmationModal
          confirmingCurrentValue={state.currentValue}
          confirmingLabel={activeAssetType}
          isOpen={isConfirmationOpen}
          isLoadingUpdate={isLoadingUpdate}
          onCancel={actions.onCancelConfirmation}
          onConfirm={confirmationProps.isReplace ? actions.onConfirmReplace : actions.onConfirmRemove}
          pendingValue={state.pendingAction?.value}
          type={confirmationProps.isReplace ? 'replace' : 'remove'}
        />
      )}

      {/* Asset List with current value and custom entry */}
      {!state.showRemoveConfirmation &&
        !state.showReplaceConfirmation &&
        (state.loadingAssets ? (
          <AssetListSkeleton activeAssetType={activeAssetType} />
        ) : (
          <AssetList
            activeAssetType={activeAssetType}
            assets={state.assets}
            currentValue={state.currentValue}
            isLoadingUpdate={isLoadingUpdate}
            manualInputValue={state.manualInputValue}
            onAssetSelect={actions.onAssetSelect}
            onManualInputChange={actions.onManualInputChange}
            onManualSubmit={actions.onManualSubmit}
            onRemoveClick={actions.onRemoveClick}
            selectingAsset={state.selectingAsset}
            selectedELDApp={state.selectedELDApp}
            selectedItem={state.selectedItem}
          />
        ))}
    </div>
  )
}
